import React from "react";
import LegalDocument from "./LegalDocument";

export default function Privacy() {
    return (
        <LegalDocument
            activePage="Privacy"
            title="42 Degrees Privacy Policy"
            effectiveDate="April 13, 2026"
            lastUpdated="April 13, 2026"
        >
            <h2>1. Introduction</h2>
            <p>
                42 Degrees Corp., a Delaware corporation with its principal place of business at 123 Front Street,
                San Diego, California 92115 ("42 Degrees," "we," "us," or "our") is committed to protecting your privacy.
                This Privacy Policy explains how we collect, use, disclose, and safeguard your personal information when
                you use our research novelty prediction scoring platform ("Platform" or "Service") accessible at
                https://fortytwodegrees.org and through our API integrations.
            </p>
            <p>This Privacy Policy applies to information we collect through:</p>
            <ul>
                <li>Our website and Platform</li>
                <li>Email and other electronic communications</li>
                <li>API interactions with approved partners</li>
                <li>Interactions at conferences, exhibitions, and other events</li>
                <li>PDF reports shared with third-party recipients (see Section 5.8)</li>
                <li>Peer review communications between submitters and reviewers (see Section 5.10)</li>
                <li>Publicly accessible showcase papers (see Section 5.9)</li>
            </ul>
            <p>
                <strong>Please read this Privacy Policy carefully.</strong> By accessing or using the Service, you
                acknowledge that you have read, understood, and agree to be bound by this Privacy Policy. If you do not
                agree with our policies and practices, do not use the Service.
            </p>

            <h2>2. Information We Collect</h2>

            <h3>2.1 Information You Provide Directly</h3>
            <p><strong>Account Registration Information.</strong> When you create an account, we collect:</p>
            <ul>
                <li>First and last name</li>
                <li>Email address</li>
                <li>Password (one-way hashed; we never store your plaintext password)</li>
                <li>Profile image (optional)</li>
                <li>Account creation date</li>
            </ul>
            <p><strong>Account Preferences.</strong> Your account includes a set of preferences you can toggle in Settings, all of which are stored on our servers. These include:</p>
            <ul>
                <li>"Store Manuscripts" — opt-in to retain uploaded PDFs after processing (off by default; see Section 4.1)</li>
                <li>Service email notifications — opt-out of processing-status emails</li>
                <li>Peer review notifications — opt-out of peer review email notifications</li>
                <li>"Open to Review" — signal that you are willing to serve as a reviewer</li>
                <li>"Paper of the Week" eligibility — opt-in for one of your manuscripts to be featured publicly on other users' dashboards (off by default; see Section 5.11)</li>
                <li>Featured manuscript visibility flags — whether your name and a downloadable PDF of the featured paper are shown</li>
                <li>Community feed opt-in — share achievement events with other users (off by default; see Section 5.12)</li>
                <li>Public profile opt-in — make your profile and public submissions visible without authentication (off by default; see Section 5.13)</li>
                <li>"Show Score History" — control whether score history is shown on your dashboard</li>
                <li>Display view preference (e.g., grid vs. list)</li>
            </ul>
            <p><strong>Manuscript Submissions.</strong> When you use the Service, you submit:</p>
            <ul>
                <li>Research manuscripts and papers (PDF format)</li>
                <li>Metadata about your submissions (title, authors)</li>
                <li>Any additional information you voluntarily provide</li>
            </ul>
            <p><strong>Peer Review Submissions.</strong> When you submit a manuscript for peer review, we collect:</p>
            <ul>
                <li>The manuscript title, authors, and any notes you provide for the reviewer</li>
                <li>Your peer review preferences (notification settings)</li>
            </ul>
            <p>When you serve as a reviewer, we collect:</p>
            <ul>
                <li>Your review scores (novelty, methodology, impact)</li>
                <li>Written feedback (summary, strengths, weaknesses, suggestions)</li>
                <li>Your recommendation (accept, minor revision, major revision, reject)</li>
                <li>Your accept/decline responses to review invitations</li>
            </ul>
            <p><strong>Feedback Form Responses.</strong> When you submit the in-app feedback form, we collect:</p>
            <ul>
                <li>Your responses to structured questions about your experience with the Service</li>
                <li>Your role/title and institution</li>
                <li>Your name and email address (optional, for follow-up purposes)</li>
            </ul>
            <p><strong>Communications.</strong> When you contact us, we collect:</p>
            <ul>
                <li>Email correspondence</li>
                <li>Support ticket information</li>
                <li>Feedback and survey responses (also forwarded to a Google Sheets workbook for triage — see Section 5.2)</li>
            </ul>

            <h3>2.2 Information Collected Automatically</h3>
            <p>
                <strong>Server-Side Activity Tracking.</strong> We maintain a persistent activity log tied to your user
                account that records the following actions, along with a timestamp and the manuscript ID where applicable:
                manuscript uploads, submission views, network graph views, similar works views, report downloads, report
                shares, manuscript certifications, manuscript deletions, feature page views (e.g., Deep Thinkers), peer
                review submissions, peer review cancellations, review assignment acceptances, review assignment declines,
                and review feedback submissions. This log is used by administrators to understand feature usage and improve
                the Service. It is retained for the lifetime of your account and deleted when you delete your account.
            </p>
            <p>
                <strong>Session Recording and Heatmap Data.</strong> We use Microsoft Clarity to capture anonymized
                session recordings, click and scroll heatmaps, and aggregated usability metrics (e.g., dead clicks, rage
                clicks, scroll depth). Clarity records visual interactions with the Platform but automatically masks form
                input fields, password fields, and any element marked as sensitive. See Section 5.2 for details about
                Microsoft Clarity as a service provider.
            </p>
            <p>
                <strong>In-App Notifications.</strong> The Platform generates per-user notifications when relevant events
                occur (e.g., manuscript scoring completed, peer review status changes, new community feed activity tied to
                your work). Each notification stores a title, message, an optional link to the related page, an optional
                manuscript reference, the icon type, the created timestamp, and read/dismissed state. Notifications are
                visible only to the user they belong to and are deleted when you delete your account or the associated
                manuscript.
            </p>
            <p>
                <strong>Community Feed Events.</strong> If you opt in to the community feed (off by default), the Platform
                automatically generates feed events when certain milestones occur on your manuscripts — for example, high
                novelty / methodology / impact / overall scores, personal-best scores, first or milestone-numbered papers,
                deep-thinker or similar-paper matches, and featured-this-week designation. Each feed event stores the event
                type, your user reference, the manuscript ID, the manuscript title, the relevant score or count, and a
                timestamp. See Section 5.12 for how these events are shared with other users.
            </p>
            <p><strong>Device and Technical Information:</strong></p>
            <ul>
                <li>IP address</li>
                <li>Browser type and version</li>
                <li>Operating system</li>
                <li>Device type and identifiers</li>
                <li>Referring/exit pages</li>
                <li>Date/time stamps</li>
            </ul>
            <p>
                <strong>Cookies and Tracking Technologies.</strong> We use cookies, web beacons, pixels, and similar
                technologies. See Section 8 for details about cookies and your choices.
            </p>

            <h3>2.3 Information from Third-Party Sources</h3>
            <p>We may obtain information from:</p>
            <ul>
                <li>
                    <strong>Google Sign-In (OAuth 2.0):</strong> When you choose to register or log in using your Google
                    account, Google provides us with your name, email address, and profile picture. Use of Google Sign-In
                    is governed by Google's Privacy Policy at https://policies.google.com/privacy.
                </li>
                <li>Publicly available academic databases and repositories</li>
                <li>Conference registration systems (if you meet us at events)</li>
            </ul>

            <h3>2.4 Information We Do NOT Collect</h3>
            <p>We do not knowingly collect:</p>
            <ul>
                <li>
                    Sensitive personal information such as racial or ethnic origin, political opinions, religious or
                    philosophical beliefs, trade union membership, genetic data, biometric data, health information, or
                    information about sexual orientation
                </li>
                <li>Personal information from individuals under 18 years of age</li>
                <li>Government-issued identification numbers</li>
                <li>Payment card data (we do not currently process payments — see Section 4 of the Terms of Service)</li>
            </ul>

            <h2>3. How We Use Your Information</h2>

            <h3>3.1 Legal Bases for Processing (GDPR)</h3>
            <p>
                For users in the European Economic Area (EEA), UK, or Switzerland, we process personal information based
                on the following legal bases:
            </p>
            <ul>
                <li><strong>Contractual Necessity:</strong> Processing necessary to provide the Service you requested</li>
                <li>
                    <strong>Legitimate Interests:</strong> Processing necessary for our legitimate business interests, such
                    as improving the Service, security, fraud prevention, and feature usage analytics
                </li>
                <li>
                    <strong>Consent:</strong> Processing based on your explicit consent, which you may withdraw at any time
                </li>
                <li><strong>Legal Obligation:</strong> Processing necessary to comply with applicable laws and regulations</li>
            </ul>

            <h3>3.2 Purposes of Data Processing</h3>
            <p><strong>Service Provision:</strong></p>
            <ul>
                <li>Create and manage your account</li>
                <li>Process manuscript submissions and generate Novelty, Methodology, and Impact scores</li>
                <li>Persist scores and analyses to your account so you can revisit them later</li>
                <li>Build and serve network graph visualizations of similar works</li>
                <li>Generate and email PDF reports you choose to share</li>
                <li>Facilitate peer review by assigning reviewers, sending invitations, and delivering feedback</li>
                <li>Provide technical support and customer service</li>
            </ul>
            <p><strong>Service Improvement:</strong></p>
            <ul>
                <li>Monitor and analyze usage patterns and trends via our internal activity log and Microsoft Clarity</li>
                <li>Diagnose technical problems and maintain security</li>
                <li>Develop new features and conduct quality assurance testing</li>
                <li>
                    <strong>NOTE:</strong> We do NOT use your manuscripts to train, fine-tune, or evaluate our algorithms
                    or any third-party models
                </li>
            </ul>
            <p><strong>Communications:</strong></p>
            <ul>
                <li>Respond to your inquiries and requests</li>
                <li>Send service-related notifications, including processing-status updates and peer review status changes</li>
                <li>Send peer review invitation emails to designated reviewers</li>
                <li>Send administrative information, such as changes to Terms or policies</li>
            </ul>
            <p><strong>Legal and Security:</strong></p>
            <ul>
                <li>Comply with legal obligations and respond to legal requests</li>
                <li>Enforce our Terms of Service</li>
                <li>Protect against fraud, unauthorized access, and security threats</li>
            </ul>

            <h2>4. Data Retention and Storage</h2>

            <h3>4.1 Manuscript File Storage</h3>
            <p>
                <strong>By default, we do NOT retain your manuscript PDF after processing.</strong> When you upload a
                manuscript, the PDF is uploaded to our cloud storage so our processing service can read it. As soon as
                processing finishes, we check your "Store Manuscripts" account setting. Because the default value of that
                setting is <strong>off</strong>, the PDF is automatically deleted immediately after processing completes.
            </p>
            <p>
                If — and only if — you have explicitly turned on "Store Manuscripts" in your account Settings, your PDF
                will remain in storage after processing so that you can download it again later. Turning the setting back
                off does not retroactively delete previously stored PDFs; you can delete those individually at any time
                using the in-app "Delete" action.
            </p>
            <p>Stored manuscripts are required for peer review eligibility. Only manuscripts that remain in cloud storage can be submitted for peer review.</p>
            <ul>
                <li><strong>Default behavior:</strong> PDF is deleted within seconds of processing completing</li>
                <li><strong>Opt-in behavior:</strong> PDF is retained only if "Store Manuscripts" is enabled in Settings</li>
                <li>
                    <strong>User-Requested Deletion:</strong> Deleting a manuscript via the in-app delete action removes
                    the manuscript metadata, all associated analyses, all similar paper records, the network graph file,
                    the Deep Thinkers content file, all peer review records, all reviewer invitations, all review feedback,
                    and the PDF (if it was stored)
                </li>
                <li>
                    <strong>Account Deletion:</strong> Deleting your account removes everything described above for every
                    manuscript you have ever submitted
                </li>
                <li>
                    <strong>Backup Retention:</strong> Backup copies in our automated backup system may persist for a
                    limited period after deletion from production
                </li>
            </ul>

            <h3>4.2 Output Storage (Scores, Analyses, Graphs)</h3>
            <p>
                Although we do not retain the PDF itself by default, the <strong>outputs</strong> generated from your
                manuscript are stored in your account so that you can revisit them later. These outputs are tied to your
                user record and remain available until you delete the manuscript or your account.
            </p>
            <p>Specifically, after processing completes we retain:</p>
            <ul>
                <li>Manuscript metadata (filename, title, authors, upload date)</li>
                <li>Novelty, Methodology, and Impact Scores plus confidence values</li>
                <li>Written text analyses for each scoring dimension</li>
                <li>A list of similar published papers (PMIDs, titles, authors, similarity values)</li>
                <li>The interactive network graph file</li>
                <li>The Deep Thinkers content file</li>
            </ul>
            <p>
                Generated PDF reports are not stored on our servers after you download them. We do store a record of each
                generated report (report ID, manuscript ID, creation date, and recipient email if shared) so that
                recipients can verify the report's authenticity.
            </p>

            <h3>4.3 Peer Review Data Storage</h3>
            <p>When you submit a manuscript for peer review, we store:</p>
            <ul>
                <li>The peer review request record (manuscript reference, status, dates, reviewer assignment)</li>
                <li>Reviewer invitation records (invitation token, reviewer email, status, expiration)</li>
                <li>Review feedback (scores, written assessment, recommendation) submitted by the assigned reviewer</li>
            </ul>
            <p>
                Peer review data is deleted when you delete the associated manuscript or your account. Reviewers cannot
                retain copies of manuscripts through the Platform after a review is cancelled or completed.
            </p>

            <h3>4.4 Account Information</h3>
            <p>We retain your account information for:</p>
            <ul>
                <li>As long as your account remains active</li>
                <li>As necessary to provide you with the Service</li>
                <li>As required to comply with legal obligations</li>
                <li>To resolve disputes and enforce our agreements</li>
            </ul>

            <h3>4.5 Reviewer Whitelist</h3>
            <p>
                We maintain a curated list of certified reviewer email addresses, names, institutions, and specialties.
                This list is managed by administrators and is not user-submitted. If you are added to the reviewer
                whitelist, your email, name, institution, and specialty are stored for the purpose of reviewer assignment
                and identification.
            </p>

            <h3>4.6 Activity Log Retention</h3>
            <p>
                Server-side activity log entries (Section 2.2) are retained for the lifetime of your account. They are
                deleted when you delete your account. Microsoft Clarity session recording data is retained according to
                Clarity's default retention policy (currently 13 months) and is governed by Microsoft's Privacy Statement.
            </p>

            <h3>4.7 Notifications and Community Feed Event Retention</h3>
            <p>
                Notification records (Section 2.2) are retained for the lifetime of your account or until you dismiss
                them. They are deleted when you delete your account and cascade-deleted when the associated manuscript is
                removed.
            </p>
            <p>
                Community feed events (Section 2.2 and Section 5.12) are retained while the associated manuscript and
                account exist. They are deleted when you delete the associated manuscript, when you delete your account,
                or — for events still visible in other users' feeds — pruned as the feed advances.
            </p>

            <h3>4.8 Retention After Account Deletion</h3>
            <p>
                When you delete your account, the following data is deleted: manuscripts, analyses, scores, similar paper
                records, network graphs, Deep Thinkers content files, peer review records, reviewer invitations, review
                feedback, activity log entries, notifications, community feed events, similar works preferences, user
                settings, profile image, and your user record. Some information may be retained longer for legal,
                compliance, or legitimate business purposes. Anonymized and aggregated data may be retained indefinitely.
                Backup copies may persist for up to 35 days after deletion from production systems.
            </p>

            <h2>5. How We Share Your Information</h2>

            <h3>5.1 We Do NOT Sell Your Information</h3>
            <p>
                We will <strong>NEVER</strong> sell, rent, or trade your personal information to third parties for their
                marketing purposes.
            </p>

            <h3>5.2 Service Providers and Sub-Processors</h3>
            <p>
                We share your information with trusted third-party service providers who assist us in operating the
                Service. The current list of sub-processors is:
            </p>
            <p><strong>Cloud Infrastructure — Amazon Web Services (AWS):</strong></p>
            <ul>
                <li>
                    Hosts our managed PostgreSQL database (AWS RDS, us-west-2 region), which contains user accounts,
                    manuscript metadata, scores, written analyses, similar paper records, peer review records, reviewer
                    invitations, review feedback, activity log entries, notification records, community feed events,
                    report records, and user settings
                </li>
                <li>
                    Provides cloud file storage (AWS S3) for manuscript PDF files <strong>only during processing</strong>,
                    and afterward only when the user has opted in via the "Store Manuscripts" setting
                </li>
                <li>
                    Provides cloud file storage for network graph files and Deep Thinkers content files for the lifetime
                    of the associated manuscript
                </li>
                <li>Provides cloud file storage for user profile images when uploaded</li>
                <li>Data is encrypted in transit and at rest using provider-managed encryption</li>
                <li>AWS data-handling practices are governed by Amazon's Privacy Notice at https://aws.amazon.com/privacy/</li>
            </ul>
            <p><strong>AI/ML Inference — DeepSeek:</strong></p>
            <ul>
                <li>
                    We send the textual content of your manuscript (extracted from the PDF) to DeepSeek's API for novelty,
                    methodology, and impact analysis. This means manuscript text is transmitted to a third-party AI
                    provider.
                </li>
                <li>
                    DeepSeek is contractually bound by their API terms to handle the data per their privacy and
                    data-handling policies. DeepSeek's privacy policy is available at https://www.deepseek.com.
                </li>
                <li>
                    We do <strong>not</strong> consent to DeepSeek using your manuscripts to train their models, and we
                    configure our API requests accordingly. However, you should be aware that your manuscript text is sent
                    to a third party for processing. If you have concerns about pre-publication confidentiality, do not
                    upload sensitive or unpublished material.
                </li>
            </ul>
            <p><strong>Behavioral Analytics — Microsoft Clarity:</strong></p>
            <ul>
                <li>Captures session recordings, heatmaps, click tracking, scroll depth, and usability metrics on the Platform</li>
                <li>Recordings automatically mask form fields and password inputs</li>
                <li>Data is processed according to Microsoft's Privacy Statement at https://privacy.microsoft.com/privacystatement</li>
                <li>You can opt out by enabling "Do Not Track" in your browser or via the cookie controls described in Section 8</li>
            </ul>
            <p><strong>Authentication — Google Sign-In (OAuth 2.0):</strong></p>
            <ul>
                <li>If you choose to sign in with Google, your name, email, and profile picture are shared between Google and 42 Degrees</li>
                <li>Use is governed by Google's Privacy Policy at https://policies.google.com/privacy</li>
            </ul>
            <p><strong>Feedback Storage — Google Sheets / Google Workspace:</strong></p>
            <ul>
                <li>
                    When you submit feedback through the in-app feedback form, your responses (and your name and email if
                    you provided them) are written to a Google Sheets workbook for internal triage by our team
                </li>
                <li>Use is governed by Google's Privacy Policy</li>
            </ul>
            <p><strong>Email Delivery — Google (Gmail SMTP):</strong></p>
            <ul>
                <li>
                    We send service emails (password reset, report-share, peer review invitations, review status
                    notifications, processing-status updates, and other administrative messages) from our company mailbox
                    deepthought@fortytwodegrees.org through Google's Gmail SMTP infrastructure.
                </li>
                <li>
                    The recipient email address and message contents are processed by Google in the course of delivery.
                    Use is governed by Google's Privacy Policy at https://policies.google.com/privacy.
                </li>
            </ul>
            <p>
                All sub-processors are bound by data-protection terms and are permitted to use your information only for
                the specified purposes.
            </p>

            <h3>5.3 Business Transfers</h3>
            <p>
                If 42 Degrees is involved in a merger, acquisition, reorganization, sale of assets, bankruptcy, or similar
                transaction, your personal information may be transferred as part of that transaction. We will notify you
                via email and/or prominent notice on our website before your information is transferred.
            </p>

            <h3>5.4 Legal Requirements</h3>
            <p>
                We may disclose your information if required to do so by law or in response to valid legal processes
                (subpoenas, court orders, search warrants), government or regulatory requests, or law enforcement requests.
                We will notify you of legal requests unless prohibited by law.
            </p>

            <h3>5.5 Protection of Rights</h3>
            <p>
                We may disclose your information when we believe in good faith that disclosure is necessary to protect our
                rights, property, or safety, or that of our users or the public; to detect, prevent, or address fraud,
                security, or technical issues; or to enforce our Terms of Service.
            </p>

            <h3>5.6 With Your Consent</h3>
            <p>We may share your information with third parties when you have given us explicit consent to do so.</p>

            <h3>5.7 Anonymized and Aggregated Data</h3>
            <p>
                We may share anonymized, de-identified, or aggregated data that cannot reasonably be used to identify you
                for research, marketing, analytics, or other purposes.
            </p>

            <h3>5.8 Recipients of Shared Reports</h3>
            <p>
                The Platform allows you to share a generated PDF report with a third party by providing their email
                address. When you do so:
            </p>
            <ul>
                <li>We send the recipient an email containing the PDF report and a verification link</li>
                <li>
                    We store the recipient's email address, the report ID, and the share timestamp in our Report record so
                    the recipient can verify the report's authenticity
                </li>
                <li>
                    Recipients are not required to create an account to view a shared report, but they may interact with
                    our verification page
                </li>
                <li>
                    If you are a recipient of a shared report and wish to have your email removed from our records, contact
                    privacy@fortytwodegrees.org
                </li>
            </ul>

            <h3>5.9 Sample and Showcase / Public Data</h3>
            <p>
                We provide a public sample submission (accessible without an account) that demonstrates the Platform using
                a fixed example manuscript. Additionally, administrators may designate certain manuscripts as "showcase"
                papers that are publicly accessible via unique shareable links without requiring authentication. Showcase
                papers display the full report including scores, written analyses, similar papers, network graph, and Deep
                Thinkers data to anyone with the link. Visitors to these public pages are subject to the same automatic
                data collection described in Section 2.2, including Microsoft Clarity session recording. No account-linked
                data is collected from anonymous visitors.
            </p>

            <h3>5.10 Peer Review Data Sharing</h3>
            <p>When you submit a manuscript for peer review:</p>
            <ul>
                <li>Your manuscript title, authors, and any notes you provide are shared with the assigned reviewer</li>
                <li>
                    If your manuscript is stored in cloud storage, the assigned reviewer can download the PDF for the
                    purpose of conducting the review
                </li>
                <li>The reviewer's name, institution, and specialty are visible to you as the submitter</li>
                <li>Once a review is complete, the reviewer's feedback (scores, written assessment, and recommendation) is shared with you</li>
            </ul>
            <p>When you serve as a peer reviewer:</p>
            <ul>
                <li>You receive the manuscript title, authors, submitter notes, and access to download the manuscript PDF</li>
                <li>Your name, institution, and specialty are shared with the manuscript submitter</li>
                <li>Your review feedback is shared with the manuscript submitter</li>
                <li>Your email address is used to send review invitations and status notifications</li>
            </ul>
            <p>
                Reviewer assignments are managed by 42 Degrees using a curated whitelist. We implement conflict-of-interest
                protections to prevent reviewers from being assigned manuscripts they authored or co-authored.
            </p>

            <h3>5.11 Paper of the Week / Featured Manuscripts</h3>
            <p>
                The Platform includes an opt-in feature that allows one of your manuscripts to be designated as a "Paper
                of the Week" and displayed on other users' dashboards. You control this through three independent settings:
            </p>
            <ul>
                <li>
                    <strong>Eligibility:</strong> You must explicitly enable "Featured Eligible" in Settings for any of
                    your manuscripts to be considered. The default is <strong>off</strong>.
                </li>
                <li>
                    <strong>Author Visibility:</strong> You separately control whether your name is shown next to the
                    featured manuscript. The default is <strong>off</strong> (anonymous).
                </li>
                <li>
                    <strong>Download Permission:</strong> You separately control whether other users can download the PDF
                    of the featured manuscript. The default is <strong>off</strong>.
                </li>
            </ul>
            <p>
                You can change any of these settings at any time. Changes take effect immediately for future featuring;
                manuscripts already featured may continue to appear until the next feed update.
            </p>

            <h3>5.12 Community Feed</h3>
            <p>
                The Platform includes an opt-in community feed that broadcasts achievement events from participating users
                to other authenticated users. The default is <strong>off</strong>; nothing about your activity appears in
                the community feed unless you turn on "Community Feed Opt-In" in Settings.
            </p>
            <p>If you opt in, the following information about your activity is visible to other authenticated users on the Platform:</p>
            <ul>
                <li>Your display name and profile image</li>
                <li>The manuscript title associated with the event</li>
                <li>The event type (e.g., high novelty score, personal best, first paper, milestone count, deep-thinkers matched, similar papers found, featured this week)</li>
                <li>The associated score value or count</li>
                <li>The event timestamp</li>
            </ul>
            <p>
                You can opt out at any time. Opting out stops generating new feed events for your activity, and past feed
                events tied to your account are removed when you delete the associated manuscript or your account.
            </p>

            <h3>5.13 Public User Profiles</h3>
            <p>
                Users may opt in to publish their profile via the "Profile Public" setting (off by default). When enabled,
                your profile page (display name, profile image, account creation date, and the subset of submissions you
                have elected to make public) becomes accessible at a stable URL without requiring viewers to authenticate.
                Disabling this setting immediately removes public access; the profile reverts to a private state and any
                attempt to access it shows a "private profile" page.
            </p>

            <h2>6. International Data Transfers</h2>

            <h3>6.1 Cross-Border Transfers</h3>
            <p>
                42 Degrees is based in the United States. Your personal information is transferred to, stored, and
                processed in the United States and other countries where our service providers operate. These countries
                may have data protection laws that differ from those in your country.
            </p>

            <h3>6.2 Safeguards for International Transfers</h3>
            <p>
                When we transfer personal information from the EEA, UK, or Switzerland to other countries, we implement
                appropriate safeguards, including Standard Contractual Clauses, data processing agreements with service
                providers, and adequacy decisions where applicable.
            </p>

            <h3>6.3 Data Storage Locations</h3>
            <ul>
                <li><strong>Primary application data, database, and file storage:</strong> United States</li>
                <li><strong>AI processing:</strong> DeepSeek API (servers may be located in jurisdictions outside the United States)</li>
                <li><strong>Behavioral analytics:</strong> Microsoft Clarity (Microsoft data centers, primarily United States)</li>
            </ul>

            <h2>7. Your Privacy Rights</h2>

            <h3>7.1 Rights for All Users</h3>
            <ul>
                <li><strong>Access:</strong> Request access to the personal information we hold about you</li>
                <li><strong>Correction:</strong> Request correction of inaccurate or incomplete information</li>
                <li><strong>Deletion:</strong> Request deletion of your personal information</li>
                <li><strong>Objection:</strong> Object to our processing of your personal information</li>
                <li><strong>Restriction:</strong> Request restriction of processing under certain circumstances</li>
                <li><strong>Opt-Out of Behavioral Analytics:</strong> Opt out of Microsoft Clarity tracking via our cookie controls or browser settings</li>
            </ul>

            <h3>7.2 Additional Rights for EEA, UK, and Swiss Users (GDPR)</h3>
            <ul>
                <li><strong>Data Portability:</strong> Request a copy of your personal information in a structured, machine-readable format</li>
                <li><strong>Withdraw Consent:</strong> Withdraw consent to processing at any time</li>
                <li><strong>Lodge a Complaint:</strong> File a complaint with your local data protection authority</li>
            </ul>

            <h3>7.3 California Privacy Rights (CCPA/CPRA)</h3>
            <ul>
                <li><strong>Right to Know:</strong> Request disclosure of personal information collected, used, or shared</li>
                <li><strong>Right to Delete:</strong> Request deletion of personal information</li>
                <li><strong>Right to Correct:</strong> Request correction of inaccurate personal information</li>
                <li><strong>Right to Opt-Out:</strong> Opt out of the sale or sharing of personal information (we do not sell)</li>
                <li><strong>Right to Limit Use of Sensitive PI:</strong> Limit the use of sensitive personal information (we do not collect any)</li>
                <li><strong>Right to Non-Discrimination:</strong> Not receive discriminatory treatment for exercising your rights</li>
            </ul>

            <h3>7.4 How to Exercise Your Rights</h3>
            <p>To exercise any of these rights:</p>
            <ul>
                <li>Email us at privacy@fortytwodegrees.org</li>
                <li>Use the in-app account deletion feature in Settings</li>
                <li>Click "unsubscribe" in any marketing email</li>
            </ul>
            <p>
                We will respond to your request within thirty (30) days (or as required by applicable law). We may need to
                verify your identity before processing your request.
            </p>

            <h2>8. Cookies and Tracking Technologies</h2>

            <h3>8.1 Types of Cookies and Storage We Use</h3>
            <ul>
                <li>
                    <strong>Essential cookies and local storage:</strong> Required for the Service to function. We use
                    browser local storage to store your authentication tokens ("ftAccessToken" and "ftRefreshToken"), a
                    cached copy of your profile image URL ("ftUserPhoto") to render the header avatar without a round-trip
                    on each navigation, and a cached unread notification count ("ftUnreadCount") to keep the bell badge
                    from flashing during page transitions. All cached values are cleared on logout or account deletion.
                    Disabling local storage will prevent you from logging in.
                </li>
                <li>
                    <strong>Analytical cookies (third-party):</strong> Microsoft Clarity sets cookies (e.g., "_clck",
                    "_clsk", "CLID", "ANONCHK", "MR", "MUID", "SM") to identify unique sessions, stitch session recordings
                    together, and aggregate metrics. These cookies do not contain personal information and are used solely
                    for usability analytics.
                </li>
            </ul>

            <h3>8.2 Third-Party Trackers</h3>
            <ul>
                <li><strong>Microsoft Clarity</strong> — session recordings, heatmaps, click and scroll metrics. See https://privacy.microsoft.com/privacystatement</li>
                <li><strong>Google Sign-In</strong> (only loaded when you choose to sign in with Google) — see https://policies.google.com/privacy</li>
            </ul>

            <h3>8.3 Cookie Management</h3>
            <p>You can control cookies and tracking through:</p>
            <ul>
                <li>
                    <strong>Browser Settings:</strong> Most browsers allow you to refuse or delete cookies. Note that
                    disabling essential cookies/local storage will prevent you from logging in.
                </li>
                <li>
                    <strong>Microsoft Clarity Opt-Out:</strong> Visit https://privacy.microsoft.com to manage your Microsoft
                    account privacy settings, or enable "Do Not Track" in your browser
                </li>
                <li>
                    <strong>EU/UK/Swiss Users:</strong> If we determine you are visiting from the EEA, UK, or Switzerland,
                    we will not load Microsoft Clarity until you provide consent. You may withdraw consent at any time by
                    clearing cookies and reloading the page.
                </li>
            </ul>

            <h3>8.4 Do Not Track Signals</h3>
            <p>
                We honor Do Not Track (DNT) signals where technically feasible. When DNT is enabled, we will not initialize
                Microsoft Clarity for your session.
            </p>

            <h2>9. Data Security</h2>

            <h3>9.1 Security Measures</h3>
            <ul>
                <li><strong>Encryption in Transit:</strong> All API requests use HTTPS / TLS</li>
                <li><strong>Encryption at Rest:</strong> Our database and file storage use provider-managed encryption at rest</li>
                <li>
                    <strong>Password Hashing:</strong> User passwords are one-way hashed with a per-password salt before
                    being stored. We never store plaintext passwords.
                </li>
                <li><strong>Authentication:</strong> Sessions are managed using signed JWT access tokens with separate refresh tokens</li>
                <li>
                    <strong>Access Controls:</strong> The application enforces role-based access control (USER vs. ADMIN).
                    Only users with the ADMIN role can view the admin analytics and activity dashboards. Reviewer access is
                    determined by membership in a curated reviewer whitelist, not by role assignment.
                </li>
                <li><strong>Network Security:</strong> Database access is restricted to our application servers; file storage buckets are not public</li>
                <li><strong>Monitoring:</strong> Server logs are retained for security and debugging purposes</li>
            </ul>

            <h3>9.2 No Guarantee of Security</h3>
            <p>
                While we use reasonable efforts to protect your information, no method of transmission over the internet
                or electronic storage is 100% secure. We cannot guarantee absolute security of your data.
            </p>

            <h3>9.3 Your Responsibility</h3>
            <ul>
                <li>Maintaining the confidentiality of your account credentials</li>
                <li>Using strong, unique passwords</li>
                <li>Notifying us immediately of any unauthorized access to your account</li>
                <li>
                    Considering carefully whether to upload pre-publication or otherwise sensitive material, given the
                    third-party AI processing described in Section 5.2 and the peer review sharing described in Section 5.10
                </li>
            </ul>

            <h3>9.4 Data Breach Notification</h3>
            <p>
                In the event of a data breach that affects your personal information, we will notify you and relevant
                authorities as required by applicable law.
            </p>

            <h2>10. Children's Privacy</h2>
            <p>
                Our Service is not intended for children under 18 years of age. We do not knowingly collect personal
                information from individuals under 18. If you are a parent or guardian and believe your child has provided
                us with personal information, please contact us at privacy@fortytwodegrees.org.
            </p>

            <h2>11. Links to Third-Party Websites</h2>
            <p>
                Our Service may contain links to third-party websites, services, or applications that are not owned or
                controlled by 42 Degrees. This Privacy Policy does not apply to third-party sites. We encourage you to
                read the privacy policies of any third-party sites you visit.
            </p>

            <h2>12. Changes to This Privacy Policy</h2>
            <p>When we make material changes to this Privacy Policy, we will:</p>
            <ul>
                <li>Update the "Effective Date" at the top of this policy</li>
                <li>Notify you via email to the address associated with your account</li>
                <li>Post a notice on our website or through the Service interface</li>
            </ul>
            <p>
                For material changes that significantly affect your rights, we will provide at least thirty (30) days'
                advance notice.
            </p>

            <h2>13. Contact Us</h2>
            <p>
                If you have questions, concerns, or requests regarding this Privacy Policy or our data practices, please
                contact us at:
            </p>
            <p className="legal-doc-address">
                <strong>42 Degrees Corp.</strong><br />
                Attention: Privacy Officer<br />
                123 Front Street<br />
                San Diego, California 92115<br />
                United States<br />
                <br />
                Email: privacy@fortytwodegrees.org<br />
                Website: https://fortytwodegrees.org
            </p>
            <p>
                By using the Service, you acknowledge that you have read and understood this Privacy Policy and agree to
                the collection, use, and disclosure of your information as described herein.
            </p>
        </LegalDocument>
    );
}
